import type { FileVersion } from "schemas/generated/fileVersion";

import React, { useState } from "react";

import { Button } from "@replo/design-system/components/button/Button";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from "@replo/design-system/components/menu/Menu";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { useNextDevServer } from "@v2/features/page-renderer/components/code-execution/context";
import { useVersionHistory } from "@v2/features/version-history/useVersionHistory";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useIsDebugMode } from "@v2/hooks/useIsDebugMode";
import {
  useEditorStoreActions,
  useViewingVersionId,
} from "@v2/store/editorStore";
import { nextJSProjectRouter } from "@v2/trpc/routes/nextJSProject";
import { Ellipsis, ExternalLink, RotateCcw } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useSearchParams } from "react-router-dom";
import { formatDateToMonthDayHour } from "replo-utils/lib/datetime";

export const VersionCardSkeleton = () => (
  <div className="flex justify-between p-3 rounded-xl gap-1.5 border border-border border-0.5">
    <div className="flex flex-col gap-1">
      <Skeleton className="max-w-58 w-full h-5" />
      <Skeleton className="max-w-18 w-full h-4" />
    </div>

    <div className="h-8 w-8" />
  </div>
);

export function VersionCard({
  version,
  className,
  currentPagePreviewVersionClassName,
}: {
  version: FileVersion;
  className?: string;
  currentPagePreviewVersionClassName?: string;
}) {
  const projectId = useCurrentProjectId();
  const [isHovering, setIsHovering] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const pageId = searchParams.get("pageId");

  const { setViewingVersionId } = useEditorStoreActions();
  const {
    handleRevertPageToVersion,
    handlePreviewVersion,
    isRevertingPageToVersion,
    isViewingVersion,
    isCurrentVersion,
  } = useVersionHistory({ fileVersionId: version.id });
  const viewingVersionId = useViewingVersionId();
  const { isSyncPending } = useNextDevServer();
  const isSyncingToVersion = isSyncPending && viewingVersionId === version.id;

  const { data: file } = nextJSProjectRouter.getPage.useQuery(
    pageId && projectId
      ? {
          projectId,
          id: version.fileId,
        }
      : skipToken,
  );

  const handleOpenPage = React.useCallback(
    (pageId: string, fileVersionId: string) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("pageId", pageId);
        return newParams;
      });
      setViewingVersionId(fileVersionId);
    },
    [setSearchParams, setViewingVersionId],
  );

  const getBackgroundStyle = () => {
    if (isCurrentPagePreviewVersion) {
      return "linear-gradient(white, white) padding-box, var(--color-gradient-ai-10) border-box";
    }

    return `${isHovering ? "linear-gradient(var(--color-sheet), var(--color-sheet))" : "linear-gradient(white, white)"} padding-box, var(--color-border) border-box`;
  };

  const isCurrentPagePreviewVersion =
    isViewingVersion || (!viewingVersionId && isCurrentVersion);

  const isOnCurrentPage = version.fileId === pageId;

  const getVersionSubtext = () => {
    if (isCurrentVersion) {
      return "Current Version";
    }

    if (version.fileId !== pageId && file?.pageRoute) {
      return file.pageRoute;
    }

    return formatDateToMonthDayHour(version.createdAt);
  };

  return (
    <div
      className={twMerge(
        "relative isolate flex items-start justify-between gap-1.5 p-3 rounded-xl border-transparent border-0.5 cursor-pointer max-w-full",
        isCurrentPagePreviewVersion && currentPagePreviewVersionClassName,
        !isOnCurrentPage && "items-center",
        className,
      )}
      style={{
        background: getBackgroundStyle(),
        maxWidth: isCurrentPagePreviewVersion ? "calc(100% - 8px)" : undefined,
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onClick={() => {
        void handlePreviewVersion();
      }}
    >
      {/* Glow border for current previewed version*/}
      {isCurrentPagePreviewVersion && (
        <AnimatePresence>
          <motion.span
            aria-hidden
            className="pointer-events-none absolute -z-1 border-transparent bg-clip-padding-box"
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              background: `${isHovering ? "linear-gradient(var(--color-sheet), var(--color-sheet))" : "linear-gradient(white, white)"} padding-box, var(--color-gradient-ai-10) border-box`,
              borderWidth: "4px",
              inset: "-4px",
              borderRadius: "16px",
            }}
            transition={{
              duration: 0.2,
              ease: "easeInOut",
            }}
          />
        </AnimatePresence>
      )}

      <div className="flex flex-col gap-1 flex-1 min-w-0 overflow-hidden">
        <span className="text-sm truncate block">{version.summary}</span>

        <span className="text-xs text-muted truncate block">
          {getVersionSubtext()}
        </span>
      </div>

      <div className="flex items-center gap-1">
        <VersionCardDebugMenu fileRevisionId={version.id} />
        {isOnCurrentPage ? (
          <Tooltip
            content={
              isCurrentVersion
                ? "You are already on this version"
                : "Restore to this version"
            }
          >
            <Button
              icon
              onClick={() => {
                void handleRevertPageToVersion();
              }}
              loading={isRevertingPageToVersion || isSyncingToVersion}
              variant="tertiary"
              size="sm"
              disabled={
                isCurrentVersion ||
                isRevertingPageToVersion ||
                isSyncingToVersion
              }
            >
              <RotateCcw />
            </Button>
          </Tooltip>
        ) : (
          <Button
            onClick={() => {
              void handleOpenPage(version.fileId, version.id);
            }}
            loading={isSyncPending}
            variant="secondary"
            size="sm"
          >
            Open Page
          </Button>
        )}
      </div>
    </div>
  );
}

function VersionCardDebugMenu({ fileRevisionId }: { fileRevisionId: string }) {
  const isDebugMode = useIsDebugMode();

  if (!isDebugMode) {
    return null;
  }

  const adminUrl = `https://admin.replo.app/dashboard/file-versions?fileRevisionId=${fileRevisionId}`;

  return (
    <Menu>
      <MenuTrigger
        render={
          <Button
            icon
            variant="tertiary"
            size="sm"
            onClick={(e) => e.stopPropagation()}
          >
            <Ellipsis />
          </Button>
        }
      />
      <MenuContent align="end" side="bottom">
        <MenuItem
          label="View in Admin"
          startEnhancer={<ExternalLink />}
          onClick={() => {
            window.open(adminUrl, "_blank");
          }}
        />
      </MenuContent>
    </Menu>
  );
}
